import {
	Box,
	Button,
	Checkbox,
	FormControl,
	FormControlLabel,
	InputLabel,
	ListItemText,
	MenuItem,
	Select,
	Stack,
	TextField,
	Typography,
	Card,
	CardContent,
	CardHeader,
	CardActions,
	Divider,
} from "@mui/material";
import { FunctionComponent, useState } from "react";
import { DateTimePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import cs from "date-fns/locale/cs";
import { isValid } from "date-fns";
import { enumValuesToArray } from "Infrastructure/Utils/ObjectUtils";
import {
	InfoWarningPlacement,
	InfoWarningType,
	InfoWarningVisibility,
	getInfoWarningPlacementDisplayName,
	getInfoWarningProducts,
	getInfoWarningTypeDisplayName,
	getInfoWarningVisibilityDisplayName,
	hasInsurancePlacement,
} from "Infrastructure/Utils/Nsure/InfoWarningUtils";
import { getProductTypeDisplayName } from "Infrastructure/Utils/Nsure/ProductUtils";
import { HubConnection, HubConnectionState } from "@microsoft/signalr";
import {
	DeviceForm,
	getActionTypeDescription,
} from "Infrastructure/Utils/Nsure/DeviceUtils";
import { InfoWarningCreateMessage } from "Infrastructure/Models/Messages/Messages";
import { useSnackbar } from "Infrastructure/Providers/SnackbarProvider/SnackbarProvider";
import { CloseDialogHandler } from "Infrastructure/Providers/DialogProvider/DialogProvider";
import { FormValue, areAllFieldsValid } from "Infrastructure/Utils/FormUtils";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { BlTooltip } from "Shared/BlTooltip";
import { BlConfirmButton } from "Shared/Buttons/BlConfirmButton";
import { v4 as uuidv4 } from "uuid";
import { addDays, startOfDay } from "date-fns";
import Autocomplete from "@mui/material/Autocomplete";
import Chip from "@mui/material/Chip";
import { ActionType } from "Infrastructure/Api/ApiCustom";
import ClearIcon from "@mui/icons-material/Clear";
import IconButton from "@mui/material/IconButton";
import ClearAllIcon from "@mui/icons-material/ClearAll";

const TEXT_MAX_LENGTH = 200;

export type InfoWarningSaveFormData = {
	isValidationShown: boolean;
	text: FormValue<string>;
	notifyClientByEmail: FormValue<boolean>;
	validFrom: FormValue<string | Date>;
	validTo: FormValue<string | Date>;
	url: FormValue<string>;
	type: FormValue<number>;
	productTypeId: FormValue<string[]>;
	description: FormValue<string>;
	visibility: FormValue<number>;
	placement: FormValue<number[]>;
};

export const getNewFormData = () => {
	return {
		isValidationShown: false,
		text: {
			value: "",
		},
		notifyClientByEmail: {
			value: false,
			isValid: true,
		},
		validFrom: {
			value: null,
		},
		validTo: {
			value: null,
		},
		url: {
			value: "",
			isValid: true,
		},
		type: {
			value: 0,
			isValid: true,
		},
		productTypeId: {
			value: [],
		},
		description: {
			value: "",
			isValid: true,
		},
		visibility: {
			value: 0,
			isValid: true,
		},
		placement: {
			value: [InfoWarningPlacement.Insurance],
			isValid: true,
		},
	};
};

type Props = {
	deviceForm: DeviceForm;
	hubConnection: HubConnection | null;
	closeDialog: CloseDialogHandler;
	onCreate: (message: InfoWarningCreateMessage) => void;
	formData: InfoWarningSaveFormData;
	setFormData: (data: InfoWarningSaveFormData) => void;
};

const InfoWarningsActionSave: FunctionComponent<Props> = (props) => {
	const {
		deviceForm,
		hubConnection,
		closeDialog,
		onCreate,
		formData,
		setFormData,
	} = props;

	const showSnackbar = useSnackbar();
	const { t } = useResource();

	const [showSpaceTooltip, setShowSpaceTooltip] = useState(false);
	const [inputValue, setInputValue] = useState("");

	const isFormDirty = () => {
		const initial = getNewFormData();
		return Object.keys(formData).some((key) => {
			const val = (formData as any)[key]?.value;
			const initVal = (initial as any)[key]?.value;
			if (Array.isArray(val) && Array.isArray(initVal)) {
				return val.join() !== initVal.join();
			}
			return val !== initVal;
		});
	};

	const handleSubmit = () => {
		if (isFormValid()) {
			if (
				!hubConnection ||
				hubConnection.state !== HubConnectionState.Connected
			) {
				showSnackbar(t(Resources.Errors.Hub.ConnectionError), "error");
				return;
			}
			const infoWarningCreateMessage: InfoWarningCreateMessage = {
				filteredDeviceNames: deviceForm.devices
					.filter((x) => x.isChecked)
					.map((x) => x.name),
				infoWarning: {
					text: formData.text.value,
					notifyClientByEmail: formData.notifyClientByEmail.value ?? false,
					validFrom: formData.validFrom.value as string,
					validTo: formData.validTo.value as string,
					url: formData.url.value,
					type: formData.type.value ?? 0,
					productTypeID: hasInsurancePlacement(formData.placement.value)
						? (formData.productTypeId.value?.join(";") ?? "")
						: "",
					description: formData.description.value,
					visibility: formData.visibility.value ?? 0,
					placement:
						formData.placement.value?.join(";") ??
						String(InfoWarningPlacement.Insurance),
				},
				requestId: uuidv4(),
			};

			onCreate(infoWarningCreateMessage);

			setFormData(getNewFormData());
		} else {
			setFormData({
				...formData,
				isValidationShown: true,
			});
		}
	};

	const handleTextFieldChange = (e: any) => {
		const { name, value } = e.target;
		const normalized = name === "description" ? value.replace(/\s/g, "") : value;
		setFormData({
			...formData,
			[name]: { value: normalized, isValid: isFieldValid(name, normalized) },
		});
	};

	const handleCheckboxChange = (e: any, checked: boolean) => {
		const { name } = e.target;
		setFormData({
			...formData,
			[name]: { value: !!checked, isValid: true },
		});
	};

	const handleDatePickerChange = (name: string, v: any, _c: any) => {
		setFormData({
			...formData,
			[name]: {
				value: v,
				isValid: isFieldValid(name, v),
			},
		});
	};

	const handleSelectChange = (e: any, _c: any) => {
		const { name, value } = e.target;
		setFormData({
			...formData,
			[name]: { value: value, isValid: isFieldValid(name, value) },
		});
	};

	const handleMultiSelectChange = (e: any, _c: any) => {
		const { name, value } = e.target;
		const newValues = typeof value === "string" ? value.split(",") : value;
		setFormData({
			...formData,
			[name]: {
				value: newValues,
				isValid: isFieldValid(name, newValues),
			},
		});
	};

	const handlePlacementChange = (e: any) => {
		const { value } = e.target;
		const newValues: number[] = (
			typeof value === "string" ? value.split(",") : value
		).map(Number);
		const insuranceSelected = hasInsurancePlacement(newValues);
		const productTypeIds = insuranceSelected
			? (formData.productTypeId.value ?? [])
			: [];
		setFormData({
			...formData,
			placement: {
				value: newValues,
				isValid: newValues.length > 0,
			},
			productTypeId: {
				value: productTypeIds,
				isValid: !insuranceSelected || productTypeIds.length > 0,
			},
		});
	};

	const handleTodayClick = () => {
		setFormData({
			...formData,
			validFrom: {
				...formData.validFrom,
				value: startOfDay(new Date()),
				isValid: true,
			},
		});
	};

	const handleTomorrowClick = () => {
		setFormData({
			...formData,
			validFrom: {
				...formData.validFrom,
				value: startOfDay(addDays(new Date(), 1)),
				isValid: true,
			},
		});
	};

	const isFieldValid = (name: string, value: any): boolean => {
		switch (name) {
			case "type":
				return value !== null;
			case "text":
				return !!value && value.length > 0 && value.length <= TEXT_MAX_LENGTH;
			case "description":
				return !!value && value?.length > 0;
			case "validFrom":
			case "validTo":
				return !!value && isValid(value);
			case "productTypeId":
				return (
					!hasInsurancePlacement(formData.placement.value) ||
					(!!value && Array.isArray(value) && value.length > 0)
				);
			case "placement":
				return !!value && Array.isArray(value) && value.length > 0;
			default:
				return true;
		}
	};

	const isFormValid = () => areAllFieldsValid(formData);

	const emailTooltip = (
		<span>
			Na vybraných instancích musí být zapnuta feature{" "}
			<em>IsSendingInfoWarningNotificationsEnabled</em> a nastaven příjemce
			e-mailu.
		</span>
	);

	const getConfirmDialogContent = () => {
		let confirmContent = (
			<>Založit infolištu bez odeslání notifikačního e-mailu?</>
		);
		if (
			!!formData.notifyClientByEmail.value &&
			deviceForm.devices
				.filter((x) => x.isChecked)
				.some((x) => x.deviceStatus.infoWarningNotificationEmailEnabled)
		) {
			const emailDevices = deviceForm.devices.filter(
				(x) => x.isChecked && x.deviceStatus.infoWarningNotificationEmailEnabled
			);

			confirmContent = (
				<>
					<span>
						Založit a odeslat notifikaci o nově přidané infoliště na následující
						e-maily?
					</span>
					<ul>
						{emailDevices.map((d, _i) => (
							<li>
								<strong>{d.name}</strong>
								<ul>
									<li>
										{d.deviceStatus.infoWarningNotificationEmailRecipients}
									</li>
								</ul>
							</li>
						))}
					</ul>
				</>
			);
		}

		return confirmContent;
	};

	const formCanBeOpened = () => {
		if (deviceForm.devices.filter((x) => x.isChecked).length === 0) {
			showSnackbar(
				t(Resources.Errors.General.Instance.NoInstanceSelected),
				"warning"
			);
			return false;
		}
		return true;
	};

	return (
		<Card
			variant="outlined"
			elevation={0}
			sx={{ my: 1 }}
			style={{ boxShadow: "none" }}
		>
			<CardHeader
				title={
					<Typography variant="subtitle1">
						{getActionTypeDescription(ActionType.InfoWarningCreate)}
					</Typography>
				}
				action={
					<IconButton
						edge="end"
						size="small"
						onClick={() => setFormData(getNewFormData())}
						disabled={!isFormDirty()}
						sx={{ mr: 1 }}
					>
						<ClearAllIcon />
					</IconButton>
				}
			/>
			<Divider />
			<CardContent sx={{ pb: 0 }}>
				<Stack spacing={1}>
					<Stack direction="row" spacing={1} alignItems="center">
						<TextField
							name="text"
							label="Text"
							variant="outlined"
							size="small"
							fullWidth
							value={formData.text.value}
							onChange={handleTextFieldChange}
							error={formData.isValidationShown && !formData.text.isValid}
							helperText={
								((formData.text.value?.length ?? 0) > TEXT_MAX_LENGTH
									? "Text je příliš dlouhý. "
									: "") + `${formData.text.value?.length ?? 0}/${TEXT_MAX_LENGTH}`
							}
							FormHelperTextProps={{
								sx: {
									textAlign: "right",
									color:
										(formData.text.value?.length ?? 0) > TEXT_MAX_LENGTH
											? "error.main"
											: undefined,
								},
							}}
						/>
					</Stack>
					<Stack direction="row" spacing={1}>
						<TextField
							name="url"
							label="URL link"
							variant="outlined"
							size="small"
							value={formData.url.value}
							inputProps={{ maxLength: 200 }}
							onChange={handleTextFieldChange}
							error={formData.isValidationShown && !formData.url.isValid}
							sx={{ flex: 1 }}
						/>
						<BlTooltip
							title="Mezera není povolena."
							open={showSpaceTooltip}
							placement="bottom-start"
						>
							<TextField
								name="description"
								label="Identifikátor / popis"
								variant="outlined"
								size="small"
								value={formData.description.value?.replace(/\s/g, "")}
								inputProps={{ maxLength: 100 }}
								onChange={handleTextFieldChange}
								error={
									formData.isValidationShown && !formData.description.isValid
								}
								sx={{ flex: 1 }}
								onKeyDown={(e) => {
									if (e.key === " ") {
										e.preventDefault();
										setShowSpaceTooltip(true);
										setTimeout(() => setShowSpaceTooltip(false), 1500);
									}
								}}
							/>
						</BlTooltip>
					</Stack>
					<Stack
							direction="row"
							spacing={1}
							useFlexGap
							alignItems="start"
							sx={{ flexWrap: "wrap" }}
						>
						<LocalizationProvider
							dateAdapter={AdapterDateFns}
							adapterLocale={cs}
						>
							<Stack sx={{ minWidth: 195 }}>
								<DateTimePicker
									name="validFrom"
									label="Platnost od"
									format={"dd.MM.yyyy HH:mm"}
									value={formData.validFrom.value}
									onChange={(v, c) => handleDatePickerChange("validFrom", v, c)}
									slotProps={{
										textField: {
											size: "small",
											error:
												formData.isValidationShown &&
												!formData.validFrom.isValid,
										},
									}}
								/>
								<Stack direction="row" justifyContent="start">
									<Button
										variant="text"
										size="small"
										color="inherit"
										sx={{ minWidth: 0, fontSize: ".7rem", ml: 1 }}
										onClick={handleTodayClick}
									>
										Dnes
									</Button>
									<Button
										variant="text"
										size="small"
										color="inherit"
										sx={{ minWidth: 0, fontSize: ".7rem" }}
										onClick={handleTomorrowClick}
									>
										Zítra
									</Button>
								</Stack>
							</Stack>
							<Stack sx={{ minWidth: 195 }}>
								<DateTimePicker
									name="validTo"
									label="Platnost do"
									format={"dd.MM.yyyy HH:mm"}
									value={formData.validTo.value}
									onChange={(v, c) => handleDatePickerChange("validTo", v, c)}
									slotProps={{
										textField: {
											size: "small",
											error:
												formData.isValidationShown && !formData.validTo.isValid,
										},
									}}
								/>
								<Stack direction="row" justifyContent="start">
									<Button
										variant="text"
										size="small"
										color="inherit"
										sx={{ minWidth: 0, fontSize: ".7rem", ml: 1 }}
										disabled={!formData.validFrom.value}
										onClick={() => {
											if (formData.validFrom.value) {
												const validFromDate =
													typeof formData.validFrom.value === "string"
														? new Date(formData.validFrom.value)
														: formData.validFrom.value;
												if (
													validFromDate instanceof Date &&
													!Number.isNaN(validFromDate.getTime())
												) {
													setFormData({
														...formData,
														validTo: {
															...formData.validTo,
															value: addDays(validFromDate, 1),
															isValid: true,
														},
													});
												}
											}
										}}
									>
										Platnost od + 1
									</Button>
								</Stack>
							</Stack>
						</LocalizationProvider>
						<Box sx={{ minWidth: 180 }}>
							<FormControl fullWidth>
								<InputLabel id="typeLabel" size="small">
									Typ upozornění
								</InputLabel>
								<Select
									name="type"
									label="Typ upozornění"
									labelId="typeLabel"
									value={formData.type.value}
									onChange={handleSelectChange}
									error={formData.isValidationShown && !formData.type.isValid}
									size="small"
								>
									{enumValuesToArray(InfoWarningType).map((item) => {
										return (
											<MenuItem value={item}>
												{getInfoWarningTypeDisplayName(item)}
											</MenuItem>
										);
									})}
								</Select>
							</FormControl>
						</Box>
						<Box sx={{ minWidth: 220 }}>
							<FormControl fullWidth>
								<InputLabel id="typeLabel" size="small">
									Viditelnost
								</InputLabel>
								<Select
									name="visibility"
									label="Viditelnost"
									labelId="visibilityLabel"
									value={formData.visibility.value}
									onChange={handleSelectChange}
									error={
										formData.isValidationShown && !formData.visibility.isValid
									}
									size="small"
								>
									{enumValuesToArray(InfoWarningVisibility).map((item) => {
										return (
											<MenuItem value={item}>
												{getInfoWarningVisibilityDisplayName(item)}
											</MenuItem>
										);
									})}
								</Select>
							</FormControl>
						</Box>
						<Box sx={{ minWidth: 240 }}>
							<FormControl fullWidth>
								<InputLabel id="placementLabel" size="small">
									Umístění
								</InputLabel>
								<Select
									name="placement"
									label="Umístění"
									labelId="placementLabel"
									multiple
									value={formData.placement.value ?? []}
									onChange={handlePlacementChange}
									error={
										formData.isValidationShown && !formData.placement.isValid
									}
									size="small"
									renderValue={(selected) =>
										(selected as number[])
											.map((x) => getInfoWarningPlacementDisplayName(x))
											.join(", ")
									}
								>
									{enumValuesToArray(InfoWarningPlacement).map((item) => {
										return (
											<MenuItem key={item} value={item}>
												<Checkbox
													checked={
														!!formData.placement.value?.includes(item)
													}
													size="small"
													style={{ marginRight: 8 }}
												/>
												<ListItemText
													primary={getInfoWarningPlacementDisplayName(item)}
												/>
											</MenuItem>
										);
									})}
								</Select>
							</FormControl>
						</Box>
						<FormControl
								sx={{
									flex: 1,
									minWidth: 260,
									"& .MuiInputBase-root": {
										height: "auto",
										minHeight: 40,
										alignItems: "flex-start",
										alignContent: "flex-start",
										flexWrap: "wrap",
										rowGap: "4px",
										py: "4px",
									},
								}}
							>
							<Autocomplete
								multiple
								disableCloseOnSelect
								options={getInfoWarningProducts().map(String)}
								value={formData.productTypeId.value ?? []}
								onChange={(_event, newValue) => {
									setFormData({
										...formData,
										productTypeId: {
											value: newValue,
											isValid: isFieldValid("productTypeId", newValue),
										},
									});
								}}
								inputValue={inputValue}
								onInputChange={(_event, newInputValue, reason) => {
									if (reason === "reset") return;
									setInputValue(newInputValue);
								}}
								disabled={!hasInsurancePlacement(formData.placement.value)}
								getOptionLabel={(option) =>
									getProductTypeDisplayName(Number(option)) ?? ""
								}
								renderInput={(params) => (
									<TextField
										{...params}
										label="Produkt"
										size="small"
										error={
											formData.isValidationShown &&
											!formData.productTypeId.isValid
										}
									/>
								)}
								renderOption={(props, option, { selected }) => (
									<li {...props} key={option} style={{ maxHeight: 30 }}>
										<Checkbox
											checked={selected}
											size="small"
											style={{ marginRight: 8 }}
										/>
										<ListItemText
											primary={getProductTypeDisplayName(Number(option))}
										/>
									</li>
								)}
								limitTags={2}
								fullWidth
								noOptionsText="Žádné možnosti"
								renderTags={(value, getTagProps) =>
									value.map((option, index) => (
										<Chip
											{...getTagProps({ index })}
											key={option}
											label={getProductTypeDisplayName(Number(option))}
											size="small"
											sx={{
												height: 24,
												maxWidth: 150,
												flexShrink: 0,
												"& .MuiChip-label": {
													fontSize: "0.75rem",
													lineHeight: "24px",
													padding: "0 8px",
													overflow: "hidden",
													textOverflow: "ellipsis",
												},
											}}
										/>
									))
								}
							/>
						</FormControl>
						<BlTooltip title={emailTooltip} placement="top">
							<FormControlLabel
								label="Zaslat notifikaci na e-mail"
								control={
									<Checkbox
										name="notifyClientByEmail"
										checked={formData.notifyClientByEmail.value ?? false}
										onChange={handleCheckboxChange}
									/>
								}
								sx={{ minWidth: 240 }}
							/>
						</BlTooltip>
					</Stack>
				</Stack>
			</CardContent>
			<CardActions sx={{ pt: 0, justifyContent: "flex-end" }}>
				<BlConfirmButton
					modalTitle={"Založení"}
					modalContent={getConfirmDialogContent()}
					onResult={(result) => {
						if (!!result) {
							handleSubmit();
						}
					}}
					disabled={!isFormValid()}
					onClick={() => formCanBeOpened()}
				>
					Uložit
				</BlConfirmButton>
			</CardActions>
		</Card>
	);
};

export default InfoWarningsActionSave;
